/**
 * Resolve once the app's entry stylesheet(s) have loaded, so the app never mounts unstyled.
 *
 * The build marks the entry <link rel="stylesheet"> with data-app-css and media="print"
 * (non-render-blocking; its onload flips media to "all"). Whether a deferred module script
 * waits for a stylesheet differs by engine - WebKit ran the entry before a body stylesheet
 * had arrived and painted the whole app unstyled (2026-09-14) - so the mount waits here
 * explicitly. Dev server: Vite injects CSS from JS, no tagged link exists, resolves at once.
 * Never hangs: a stylesheet that errors or takes longer than the timeout releases the mount
 * (an unstyled app beats no app).
 */
export function appCssReady(timeoutMs = 12000, doc: Document = document): Promise<void> {
  const links = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[data-app-css]'));
  const loaded = (l: HTMLLinkElement): boolean => {
    if (l.media === 'all') return true;
    try {
      return !!(l.sheet && l.sheet.cssRules && l.sheet.cssRules.length > 0);
    } catch {
      return !!l.sheet;
    }
  };
  if (!links.length || links.every(loaded)) return Promise.resolve();
  return new Promise<void>(resolve => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const check = () => {
      if (links.every(loaded)) finish();
    };
    links.forEach(link => {
      const el = link;
      el.addEventListener('load', () => {
        el.media = 'all';
        check();
      });
      el.addEventListener('error', finish);
    });
    // The load event can have fired before this ran; poll the sheet as a backstop.
    const poll = setInterval(() => {
      check();
      if (done) clearInterval(poll);
    }, 100);
    setTimeout(() => {
      clearInterval(poll);
      finish();
    }, timeoutMs);
  });
}
